// Add / edit bill modal. Also owns the "cascade scope" AlertDialog that asks
// whether a change to a recurring bill's amount and/or due date should apply
// to just this occurrence or every future occurrence in the chain.
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { backendToUiStatus, uiToBackendStatus } from "@/lib/bills-util";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function BillModal({ bill, fundingSources, existingBills = [], onClose, onSaved, onDelete }) {
  const [form, setForm] = useState(bill || {
    name: "", due_date: "", estimated_due_amount: "", paid_amount: 0, status: "unpaid",
    is_recurring: false, recurrence_frequency: "monthly", funding_source_id: "", notes: "", is_autopay: false, pay_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // When editing a RECURRING bill and the amount and/or due date changed, we
  // show a single AlertDialog asking whether the change(s) should apply to
  // just this occurrence or every future occurrence in the chain. Other
  // cascade-safe fields (name, funding source, autopay, pay url, notes,
  // frequency) always cascade silently — that's the whole point of "recurring".
  const [scopeDialog, setScopeDialog] = useState(null);
  // Name-collision confirmation. When CREATING a new bill (not editing) with a
  // name that matches an existing RECURRING chain, we prompt the user before
  // submitting so they don't unknowingly stack a one-time bill on top of an
  // existing chain (Apria bug, 2026-03).
  const [nameCollision, setNameCollision] = useState(null);
  // Only relevant when editing an existing recurring bill.
  const hiddenDatesCount = (bill?.deleted_dates || []).length;

  const restoreHidden = async () => {
    if (!bill?.id) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/bills/${bill.id}/restore-hidden-dates`);
      const n = data?.tombstones_cleared || 0;
      toast.success(
        n === 0
          ? "No hidden dates to restore"
          : `Restored ${n} hidden date${n === 1 ? "" : "s"} for ${bill.name}`
      );
      // Signal parent to refetch — dates_restored may include newly re-created rows.
      onSaved && onSaved(bill);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    // Name-collision guard on CREATE: warn if the new bill's name matches an
    // existing RECURRING chain the user already owns. Prevents the silent
    // "Apria one-time added on top of an existing recurring Apria chain" trap
    // where Sept looks correct but future months keep showing the old chain.
    if (!bill) {
      const trimmed = (form.name || "").trim().toLowerCase();
      const collision = trimmed
        ? existingBills.find(
            (b) => b.is_recurring && (b.name || "").trim().toLowerCase() === trimmed,
          )
        : null;
      if (collision && !nameCollision) {
        setNameCollision({ existing: collision });
        return;
      }
    }
    // On an existing RECURRING bill, if the amount OR due date changed, ask
    // the user to pick the scope before we commit. Other cascade-safe fields
    // silently cascade to future unpaid rows via the backend.
    if (bill && bill.is_recurring) {
      const newAmt = Number(form.estimated_due_amount);
      const oldAmt = Number(bill.estimated_due_amount);
      const amountChanged = Number.isFinite(newAmt) && Number.isFinite(oldAmt) && newAmt !== oldAmt;
      const dateChanged = form.due_date && bill.due_date && form.due_date !== bill.due_date;
      if (amountChanged || dateChanged) {
        setScopeDialog({
          amountChanged, dateChanged, newAmt, oldAmt,
          newDate: form.due_date, oldDate: bill.due_date,
        });
        return;
      }
    }
    await commitSave();
  };

  const commitSave = async (scope) => {
    setSaving(true);
    try {
      const dateChanged = bill && form.due_date && bill.due_date && form.due_date !== bill.due_date;
      const cascadeDate = !!bill?.is_recurring && dateChanged && scope === "all_future";

      // For recurring bills with "all future" date change, first shift the
      // whole chain via /bills/{id}/reschedule (scope=future). That handler
      // knows how to walk siblings, clamp day-of-month, and tombstone
      // vacated slots. Then PATCH the remaining fields.
      if (cascadeDate) {
        await api.post(`/bills/${bill.id}/reschedule`, {
          new_due_date: form.due_date,
          scope: "future",
        });
      }

      const payload = {
        name: form.name,
        // Only include due_date in the PATCH when we did NOT already push a
        // chain-wide reschedule. When we did, the pivot row's date is already
        // correct and re-sending it would tombstone it as an old_due.
        ...(cascadeDate ? {} : { due_date: form.due_date }),
        estimated_due_amount: Number(form.estimated_due_amount),
        paid_amount: Number(form.paid_amount || 0),
        status: form.status, is_recurring: !!form.is_recurring,
        recurrence_frequency: form.is_recurring ? form.recurrence_frequency : null,
        funding_source_id: form.funding_source_id || null,
        notes: form.notes || null,
        is_autopay: !!form.is_autopay,
        pay_url: form.pay_url ? form.pay_url.trim() : null,
        ...(scope ? { cascade_scope: scope } : {}),
      };
      const { data } = bill
        ? await api.patch(`/bills/${bill.id}`, payload)
        : await api.post("/bills", payload);
      toast.success(bill
        ? scope === "occurrence"
          ? "Bill updated (this occurrence only)"
          : scope === "all_future"
            ? "Bill updated — future occurrences also updated"
            : "Bill updated"
        : "Bill added");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setScopeDialog(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="card-soft p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="bill-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-medium">{bill ? "Edit bill" : "Add bill"}</h2>
          <button type="button" className="btn-ghost !p-2" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Name</label>
            <input required className="input-soft" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="bill-modal-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Due date</label>
            <input required type="date" className="input-soft" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} data-testid="bill-modal-date" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Amount</label>
            <input required type="number" step="0.01" className="input-soft" value={form.estimated_due_amount} onChange={(e) => setForm({...form, estimated_due_amount: e.target.value})} data-testid="bill-modal-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Status</label>
            <select className="input-soft" value={backendToUiStatus(form.status)}
              onChange={(e) => setForm({...form, status: uiToBackendStatus(e.target.value)})}
              data-testid="bill-modal-status">
              <option value="Unpaid">Unpaid</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Paid amount</label>
            <input type="number" step="0.01" className="input-soft" value={form.paid_amount} onChange={(e) => setForm({...form, paid_amount: e.target.value})} />
          </div>
          <div className="col-span-2 flex items-center gap-3 p-3 rounded-xl bg-[#F9F8F6]">
            <input type="checkbox" id="autopay" checked={!!form.is_autopay} onChange={(e) => setForm({...form, is_autopay: e.target.checked})} data-testid="bill-modal-autopay" />
            <label htmlFor="autopay" className="text-sm text-[#2C302E] flex-1">
              <span className="font-medium">Autopay bill</span>
              <span className="block text-xs text-[#59605C] mt-0.5">Auto-debits on the due date. It’ll appear in the Paid column with a <span className="text-[#8C3A30] font-medium">Not Cleared</span> tag until you confirm the debit in your bank statement.</span>
            </label>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="recurring" checked={!!form.is_recurring} onChange={(e) => setForm({...form, is_recurring: e.target.checked})} data-testid="bill-modal-recurring" />
            <label htmlFor="recurring" className="text-sm text-[#2C302E]">Recurring bill</label>
            {form.is_recurring && (
              <select className="input-soft ml-2 w-40" value={form.recurrence_frequency || "monthly"} onChange={(e) => setForm({...form, recurrence_frequency: e.target.value})}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
              </select>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Funding source (optional)</label>
            <select className="input-soft" value={form.funding_source_id || ""} onChange={(e) => setForm({...form, funding_source_id: e.target.value})}>
              <option value="">—</option>
              {fundingSources.map((fs) => <option key={fs.id} value={fs.id}>{fs.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Pay online (URL — optional)</label>
            <input
              type="url"
              className="input-soft"
              placeholder="https://your-provider.com/pay"
              value={form.pay_url || ""}
              onChange={(e) => setForm({...form, pay_url: e.target.value})}
              data-testid="bill-modal-pay-url"
            />
            <div className="text-[11px] text-[#8A918D] mt-1">If you enter a link, a small &ldquo;Pay online&rdquo; icon shows up next to this bill in the register for one-click access.</div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Notes</label>
            <textarea
              className="input-soft min-h-[72px] py-2 resize-y"
              rows={3}
              value={form.notes || ""}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Reminders, renewal date, rate history, account #, negotiation notes…"
              data-testid="bill-modal-notes"
            />
            <div className="text-[11px] text-[#8A918D] mt-1">Private to you. Shows up on the bill row on the Bills page.</div>
          </div>
        </div>
        {bill?.is_recurring && hiddenDatesCount > 0 && (
          <div className="mt-4 p-3 rounded-xl bg-[#FCE3C6] border border-[#EBC7A0]" data-testid="bill-modal-hidden-dates">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-widest text-[#7A4E1F]">
                  {hiddenDatesCount} hidden date{hiddenDatesCount === 1 ? "" : "s"}
                </div>
                <p className="text-xs text-[#8C5D30] mt-1">
                  Occurrences of this recurring bill were previously deleted or rescheduled
                  and are being skipped by the 12-month materializer. Restore them if that
                  was a mistake.
                </p>
              </div>
              <button
                type="button"
                className="btn-secondary shrink-0 text-xs"
                onClick={restoreHidden}
                disabled={restoring}
                data-testid="bill-modal-restore-hidden"
              >
                {restoring ? "Restoring…" : "Restore hidden dates"}
              </button>
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-between items-center gap-2 flex-wrap">
          {bill && onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(bill)}
              disabled={saving}
              className="text-xs font-semibold text-[#8C3A30] hover:text-[#7A2E24] hover:underline underline-offset-4 decoration-dotted disabled:opacity-50"
              data-testid="bill-modal-delete"
              title={bill.is_recurring
                ? "Delete this bill — you'll be asked whether to delete just this occurrence or all future ones"
                : "Delete this bill"}
            >
              {bill.is_recurring ? "Delete bill…" : "Delete bill"}
            </button>
          ) : <span />}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="bill-modal-save">
              {saving ? "Saving…" : "Save bill"}
            </button>
          </div>
        </div>
      </form>

      <AlertDialog
        open={scopeDialog !== null}
        onOpenChange={(open) => { if (!open) setScopeDialog(null); }}
      >
        <AlertDialogContent data-testid="bill-modal-scope-dialog" onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply changes to future occurrences?</AlertDialogTitle>
            <AlertDialogDescription>
              You changed <span className="font-semibold text-[#2C302E]">{form.name || "this bill"}</span>:
              <ul className="mt-2 space-y-1 text-[#2C302E]">
                {scopeDialog?.amountChanged && (
                  <li>
                    · Amount <span className="font-semibold">${scopeDialog?.oldAmt?.toFixed(2)}</span>
                    {" → "}
                    <span className="font-semibold">${scopeDialog?.newAmt?.toFixed(2)}</span>
                  </li>
                )}
                {scopeDialog?.dateChanged && (
                  <li>
                    · Due date <span className="font-semibold">{formatDate(scopeDialog?.oldDate)}</span>
                    {" → "}
                    <span className="font-semibold">{formatDate(scopeDialog?.newDate)}</span>
                    {" "}<span className="text-xs text-[#8A918D]">(future occurrences shift to the new day-of-month)</span>
                  </li>
                )}
              </ul>
              <div className="mt-3">
                Apply to every future unpaid occurrence in this recurring series, or only to this one bill?
                Paid/cleared history is never touched either way.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <AlertDialogCancel data-testid="bill-modal-scope-cancel">Cancel</AlertDialogCancel>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={saving}
                onClick={() => commitSave("occurrence")}
                data-testid="bill-modal-scope-occurrence"
              >
                Just this occurrence
              </button>
              <AlertDialogAction
                onClick={() => commitSave("all_future")}
                data-testid="bill-modal-scope-all-future"
                className="bg-[#4A6148] text-white hover:bg-[#3E5240]"
              >
                This and all future
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Name-collision dialog — CREATE only. Fires when the new bill's name
          exactly matches an existing RECURRING chain the user already owns.
          Prevents the silent "one-time bill on top of a recurring chain"
          scenario where future months keep showing the old chain's rows. */}
      <AlertDialog
        open={nameCollision !== null}
        onOpenChange={(open) => { if (!open) setNameCollision(null); }}
      >
        <AlertDialogContent data-testid="bill-modal-name-collision-dialog" onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>You already have a recurring &ldquo;{nameCollision?.existing?.name}&rdquo;</AlertDialogTitle>
            <AlertDialogDescription>
              A recurring bill named{" "}
              <span className="font-semibold text-[#2C302E]">{nameCollision?.existing?.name}</span>{" "}
              already exists in your bill list
              {nameCollision?.existing?.estimated_due_amount ? (
                <> at <span className="font-semibold text-[#2C302E]">${Number(nameCollision.existing.estimated_due_amount).toFixed(2)}</span></>
              ) : null}
              . Adding this new one won&rsquo;t affect the existing recurring series — both bills will show side-by-side.
              {" "}If you meant to edit the recurring one, cancel and click it in the list instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <AlertDialogCancel data-testid="bill-modal-name-collision-cancel">Cancel — I&rsquo;ll edit the recurring one</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const c = nameCollision; setNameCollision({ ...c, confirmed: true }); commitSave(); }}
              data-testid="bill-modal-name-collision-confirm"
              className="bg-[#4A6148] text-white hover:bg-[#3E5240]"
            >
              Add anyway (keep both)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
